/**
 * O campo com cadeado da trilha de infraestrutura — o encontro da F5 com a F6, na tela.
 *
 * É o único campo do motor cujo valor **não** vai para `resposta.valor`. Ele existe porque a
 * alternativa era a senha do banco do ERP do cliente virar uma linha de jsonb em claro, lida por
 * quem monta o pacote e copiada para a descrição da issue no Jira — que é literalmente o que o
 * campo legado "Configurações SMTP" faz hoje, em 59 issues medidas.
 *
 * O que ele faz de diferente de todos os outros controles:
 *
 * · **Não chama `aoMudar`, nunca.** O que a pessoa digita não entra no mapa de pendentes, não
 *   entra no `PUT` do rascunho e não encosta no formulário. Ele sai daqui direto para
 *   `PUT /cofre/credenciais/{pergunta}`, que cifra antes de gravar. O servidor cobra a mesma
 *   regra do outro lado: o salvamento de rascunho recusa qualquer conteúdo numa pergunta de
 *   credencial que não seja o reenvio idêntico da referência já gravada.
 *
 * · **Não usa `useMutation`.** O react-query guarda `variables` de cada mutação no
 *   `MutationCache`, que sobrevive à desmontagem — a senha ficaria num cache global do navegador
 *   depois de a tela sumir. A escrita é chamada direta e o que fica em estado é um booleano.
 *
 * · **Não relê o que gravou.** Depois de guardar, o campo é esvaziado e o que aparece é o
 *   metadado mascarado que o servidor devolve: que há senha guardada, quando, sob que custódia e
 *   até quando vale. Não existe caminho daqui de volta ao valor que não passe por
 *   `credencial:revelar` e por uma linha em `segredo_revelacao`.
 *
 * A revelação aparece para quem tem a permissão — e some para quem não tem. **Isso é cortesia,
 * não defesa**: quem nega é o serviço do cofre, e é lá que o teste negativo roda. Esconder um
 * botão que sempre devolveria 403 evita ensinar a pessoa a ignorar recusa; não protege nada.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Aviso, Botao, CampoDeSenha, Chip } from "../ui";
import { RevelacaoDoSegredo } from "../cofre";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import {
  CHAVE_RAIZ_DO_COFRE,
  PERMISSAO_DE_REVELAR,
  formatarInstante,
  guardarCredencial,
  limparCredencial,
  useEscritaDoCofre,
} from "../../nucleo/cofre";
import {
  CHAVE_RAIZ_DO_KICKOFF,
  referenciaDoSegredo,
  type SegredoNaPergunta,
} from "../../nucleo/kickoff";
import type { PropsDoControle } from "./controles";

export function CampoDeCredencial({ pergunta, valor, erro, desabilitado }: PropsDoControle) {
  const cliente = useQueryClient();
  const sessao = useSessao();
  const escrita = useEscritaDoCofre();
  const [senha, setSenha] = useState("");
  const [trocando, setTrocando] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);

  const segredo = pergunta.config.segredo;
  const guardado = segredo?.guardado === true;
  const referencia = referenciaDoSegredo(valor);
  /* Descartada, a credencial mantém a resposta (é o registro de que o cliente a entregou naquele
     dia), então a referência continua aí — mas não há mais material cifrado para decifrar, e o
     servidor responde 409. Oferecer o botão transformaria uma recusa esperada em erro na cara de
     quem clicou. `guardado` é o que separa os dois casos. */
  const podeRevelar =
    guardado && referencia !== null && podeNaSessao(sessao.data, PERMISSAO_DE_REVELAR);

  /* A tela releu o cofre e o motor: a gravação muda o progresso da trilha, o que a revisão do
     consultor mostra e o placar da custódia. Nenhuma das três passa por esta requisição. */
  function releuTudo() {
    void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_KICKOFF });
    void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_COFRE });
  }

  async function guardar() {
    if (!senha) return;
    const feito = await escrita.executar(() => guardarCredencial(pergunta.id, senha));
    if (feito) {
      // A senha sai do estado ANTES de qualquer outra coisa: a partir daqui o portal não tem mais
      // como mostrá-la, e é assim que tem de ser.
      setSenha("");
      setTrocando(false);
      releuTudo();
    }
  }

  /* Remover chama `DELETE /cofre/credenciais/{pergunta}`, e não um rascunho com `null`. São
     operações diferentes com o mesmo gesto na tela: o rascunho esvaziaria a resposta e deixaria
     o material cifrado no banco sem ninguém apontando para ele; o descarte zera o material e
     carimba a data. O registro de quem já viu a credencial fica nos dois casos — ele é
     append-only e não some com ela. */
  async function remover() {
    const feito = await escrita.executar(() => limparCredencial(pergunta.id));
    if (feito) {
      setConfirmandoRemocao(false);
      releuTudo();
    }
  }

  const editando = !guardado || trocando;

  return (
    <div
      className="campo-de-cofre"
      data-testid={`credencial-${pergunta.id}`}
      data-guardado={guardado ? "sim" : "nao"}
    >
      <p className="campo-de-cofre__marca">
        <span className="etiqueta etiqueta--sensivel">vai para o cofre</span>
        {guardado ? (
          <Chip estado="ok" data-testid="credencial-guardada">
            senha guardada
          </Chip>
        ) : null}
      </p>

      {guardado && segredo ? (
        <CredencialGuardada segredo={segredo} enunciado={pergunta.enunciado} />
      ) : null}

      {!guardado && segredo?.descartado_em ? (
        <Aviso tom="atencao" className="mb-3" data-testid="credencial-descartada">
          <b>
            A senha que estava aqui foi descartada em{" "}
            {formatarInstante(segredo.descartado_em) ?? "data não registrada"}.
          </b>{" "}
          O registro de quem a viu enquanto ela existiu continua guardado. Se a GEOvendas ainda
          precisa deste acesso, informe a senha atual abaixo.
        </Aviso>
      ) : null}

      {editando ? (
        <>
          <CampoDeSenha
            rotulo={pergunta.enunciado}
            obrigatorio={pergunta.obrigatoria}
            valor={senha}
            aoMudar={setSenha}
            /* Não é a senha de quem digita: é a do servidor do cliente. `off` impede o
               gerenciador do navegador de oferecer para guardá-la no cofre pessoal de quem
               preencheu — reduz o risco sem eliminá-lo, porque parte dos navegadores ignora. */
            autoComplete="off"
            desabilitado={desabilitado || escrita.executando}
            ajuda={pergunta.ajuda ?? undefined}
            /* A recusa do servidor vence a do motor: `escrita.erro` é a resposta ao último ato da
               pessoa (o clique em guardar), enquanto `erro` é o impedimento do formulário
               inteiro — "falta responder", que some no instante em que ela guardar. */
            erro={escrita.erro ?? erro ?? undefined}
          />
          {desabilitado ? null : (
            <div className="campo-de-cofre__acoes">
              <Botao
                tamanho="pequeno"
                disabled={escrita.executando || senha.length === 0}
                data-testid="guardar-credencial"
                onClick={() => void guardar()}
              >
                {escrita.executando ? "Guardando…" : "Guardar no cofre"}
              </Botao>
              {trocando ? (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  disabled={escrita.executando}
                  onClick={() => {
                    setSenha("");
                    setTrocando(false);
                    escrita.limparErro();
                  }}
                >
                  Cancelar
                </Botao>
              ) : null}
              <p className="campo-de-cofre__nota">
                Esta senha não é salva junto com o resto do formulário — ela só é gravada quando
                você clicar aqui.
              </p>
            </div>
          )}
        </>
      ) : null}

      {guardado && !editando && !desabilitado ? (
        <div className="campo-de-cofre__acoes">
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            disabled={escrita.executando}
            data-testid="trocar-credencial"
            onClick={() => {
              escrita.limparErro();
              setTrocando(true);
            }}
          >
            Trocar a senha
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            disabled={escrita.executando}
            data-testid="remover-credencial"
            onClick={() => setConfirmandoRemocao(true)}
          >
            Remover do cofre
          </Botao>
        </div>
      ) : null}

      {confirmandoRemocao ? (
        <Aviso tom="atencao" className="mt-3" data-testid="confirmar-remocao">
          <b>Remover apaga a senha do cofre e esvazia esta resposta.</b> O registro de quem já a
          viu continua — ele é append-only e não some com a credencial. Se a pergunta for
          obrigatória, a trilha volta a ficar impedida de ser enviada.
          <span className="confirmacao__acoes">
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => setConfirmandoRemocao(false)}
            >
              Cancelar
            </Botao>
            <Botao
              tamanho="pequeno"
              disabled={escrita.executando}
              data-testid="confirmar-remocao-botao"
              onClick={() => void remover()}
            >
              {escrita.executando ? "Removendo…" : "Remover"}
            </Botao>
          </span>
        </Aviso>
      ) : null}

      {!editando && escrita.erro ? (
        <p className="campo__erro" role="alert" data-testid="erro-da-credencial">
          {escrita.erro}
        </p>
      ) : null}

      {/* A revelação só é oferecida a quem a sessão declara ter `credencial:revelar` — hoje, só
          o papel de infraestrutura, e a matriz da spec §4 diz que ela não é concedível por
          exceção. Quem responde o formulário (admin do cliente, TI) entrega a senha e não a lê
          de volta: é o que separa "guardei uma credencial" de "tenho um campo de texto bonito". */}
      {podeRevelar && referencia ? (
        <div className="campo-de-cofre__revelacao">
          <RevelacaoDoSegredo
            segredoId={referencia}
            rotulo={segredo?.rotulo || pergunta.enunciado}
            desabilitado={escrita.executando}
            aoRevelar={releuTudo}
          />
        </div>
      ) : null}
    </div>
  );
}

/** O que dá para dizer sobre a credencial guardada sem dizer o que ela é. */
function CredencialGuardada({
  segredo,
  enunciado,
}: {
  segredo: SegredoNaPergunta;
  enunciado: string;
}) {
  const guardadoEm = formatarInstante(segredo.guardado_em ?? null);
  const expiraEm = formatarInstante(segredo.expira_em ?? null);

  return (
    <>
      <p className="campo__rotulo">{enunciado}</p>
      <p className="campo-de-cofre__estado" data-testid="estado-da-credencial">
        <b>{segredo.rotulo || "Credencial"}</b> está guardada, cifrada, com uma chave desta
        empresa.{guardadoEm ? ` Recebemos em ${guardadoEm}.` : ""} Ela não volta a aparecer nesta
        tela: quem da GEOvendas precisar dela informa o motivo, e isso fica registrado.
      </p>
      {expiraEm ? (
        <p className="campo-de-cofre__nota" data-testid="validade-da-credencial">
          Prazo de retenção combinado: até {expiraEm}. Depois disso ela deixa de ser revelável.
        </p>
      ) : null}
      {segredo.custodia_provisoria ? (
        /* O fato continua disponível para quem entregou a senha — era a intenção original e ela
           se mantém. O que mudou é o volume: ao lado do campo, um alarme amarelo sobre uma
           fragilidade que o cliente não pode corrigir só produz medo, e ele acabou de digitar a
           senha do servidor dele. Recolhido, quem quiser saber abre; quem não quiser não leva
           um susto no pior momento da relação. O aviso em tom de alerta continua inteiro no
           console interno, que é lido por quem PODE agir sobre ele. */
        <details className="campo-de-cofre__custodia" data-testid="credencial-sob-provisoria">
          <summary>Como esta senha é guardada</summary>
          <p>
            A chave que protege esta senha hoje fica no próprio servidor do portal, e não no
            serviço gerenciado que o projeto prevê. Estamos migrando; quando a chave definitiva
            entrar, esta credencial é re-cifrada. Se preferir, troque a senha do seu lado depois
            da implantação.
          </p>
        </details>
      ) : null}
    </>
  );
}
